// Vertical Type Mask Tool -- creates a selection in the shape of vertical text.
// Photoshop: T -> Vertical Type Mask Tool.

import { screenToDoc } from "../render/Viewport";
import { SetSelectionCommand } from "../commands/SetSelectionCommand";
import { Selection } from "../selection/Selection";
import type { PointerSample, Tool, ToolContext } from "./Tool";
import { noopTextRasterizer, type TextRasterizerFn } from "./HorizontalTypeMaskTool";

export interface TypeMaskToolCallbacks {
  requestText: (x: number, y: number) => Promise<string | null>;
}

export class VerticalTypeMaskTool implements Tool {
  readonly id = "type-mask-vertical";
  readonly shortcut = "T";
  private callbacks: TypeMaskToolCallbacks | null = null;

  fontFamily = "Arial";
  fontSize = 24;
  fontWeight = 400;
  fontStyle = "normal";

  /** Set by main.ts to rasterize text via Canvas2D. */
  textRasterizer: TextRasterizerFn | null = null;

  setCallbacks(callbacks: TypeMaskToolCallbacks): void {
    this.callbacks = callbacks;
  }

  async onPointerDown(ctx: ToolContext, s: PointerSample): Promise<void> {
    if (!ctx.doc || !this.callbacks) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const text = await this.callbacks.requestText(p.x, p.y);
    if (!text || !ctx.doc) return;

    const sel = this.textToSelection(ctx.doc.width, ctx.doc.height, text, p.x, p.y);
    if (sel && !sel.isEmpty()) {
      ctx.emit(new SetSelectionCommand(sel));
    }
  }

  private textToSelection(docW: number, docH: number, text: string, x: number, y: number): Selection | null {
    const fontCss = `${this.fontStyle} ${this.fontWeight} ${this.fontSize}px "${this.fontFamily}"`;
    const rasterize = this.textRasterizer ?? noopTextRasterizer;
    const mask = rasterize(docW, docH, text, x, y, fontCss, true, this.fontSize * 1.2);
    return Selection.fromMask(docW, docH, mask);
  }
}
